import { z } from 'zod';

/**
 * ITU-T E.164 canonical phone number regex.
 *
 * Matches: leading `+`, first digit 1–9, followed by 7–14 more ASCII
 * digits (total 8–15 digits after the `+`).
 *
 * Mirrors the DB CHECK constraint `stores_whatsapp_e164_chk` /
 * `stores_phone_e164_chk` in
 * `supabase/migrations/20260418000000_f007_contact_methods.sql` verbatim.
 * A client validation pass guarantees DB accept, and vice versa. Do not
 * diverge without updating both layers together (design §3.2 + §12.12 +
 * §12.22).
 *
 * The bracket class is explicit ASCII `[0-9]` — NOT `\d`. JavaScript's
 * `\d` is ASCII-only by default (without the `/u` Unicode flag), but we
 * stay with explicit `[0-9]` to document the intent and to be robust
 * against future regex-flavor changes. Arabic-Indic digits
 * (U+0660–U+0669) and Extended Arabic-Indic digits (U+06F0–U+06F9) are
 * correctly rejected — see the test case in `phone.test.ts`.
 */
export const E164_REGEX = /^\+[1-9][0-9]{7,14}$/;

/**
 * Nullable E.164 Zod schema for a single phone-number form field.
 *
 * `.trim()` drops surrounding whitespace; `.transform()` normalises the
 * empty string (`''`) to `null` so React Hook Form's onChange wiring
 * (`''` on clear) round-trips cleanly into the service layer which
 * writes `null` to the DB.
 *
 * The `.refine()` error message is a translation key — the form layer
 * renders `admin.storeSettings.errors.whatsappInvalid` or `.phoneInvalid`
 * based on field identity rather than relying on this default key, but
 * the default keeps standalone usages meaningful.
 */
export const phoneSchemaNullable = z
  .string()
  .trim()
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .refine((v) => v === null || E164_REGEX.test(v), {
    message: 'admin.storeSettings.errors.invalid',
  });

export const phoneSchemaRequired = z
  .string()
  .trim()
  .refine((v) => E164_REGEX.test(v), {
    message: 'admin.couriers.errors.phoneInvalid',
  });
